import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyNumbers,
  fetchSmsCode,
  cancelVirtualNumber,
} from "../store/slices/virtualNumberSlice.js";
import { GlassCard } from "../components/ui/GlassCard.jsx";
import { CountryFlag } from "../components/ui/CountryFlag.jsx";
import { FiRefreshCcw, FiMessageCircle, FiCopy, FiX } from "react-icons/fi";
import { getServiceIcon } from "../utils/serviceIcons.js";
import toast from "react-hot-toast";

const mapStatusClasses = {
  active: "bg-emerald-100 text-emerald-700",
  completed: "bg-sky-100 text-sky-700",
  expired: "bg-rose-100 text-rose-700",
  cancelled: "bg-slate-100 text-slate-700",
};

const parseHeroDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== "string") return new Date(value);

  const utcMatch = value.match(
    /^([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2}):([0-9]{2})(?:\.[0-9]+)?$/,
  );
  if (utcMatch) {
    const [, year, month, day, hour, minute, second] = utcMatch;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      ),
    );
  }

  return new Date(value);
};

const formatCountdown = (purchase, now) => {
  if (!purchase) return "Unknown";

  const { expirationMinutes, activationTime, activationEndTime } = purchase;

  // If both activation times are provided as pure clock values, compute the interval using only
  // HH:MM:SS without timezone conversion.
  const clockOnlyPattern = /^[0-9]{2}:[0-9]{2}:[0-9]{2}$/;
  if (
    activationTime &&
    activationEndTime &&
    typeof activationTime === "string" &&
    typeof activationEndTime === "string" &&
    clockOnlyPattern.test(activationTime) &&
    clockOnlyPattern.test(activationEndTime)
  ) {
    const m1 = activationTime.match(clockOnlyPattern);
    const m2 = activationEndTime.match(clockOnlyPattern);
    if (m1 && m2) {
      const [h1, min1, s1] = m1[0].split(":").map(Number);
      const [h2, min2, s2] = m2[0].split(":").map(Number);

      // Compute interval in seconds from clock times only
      let t1 = h1 * 3600 + min1 * 60 + s1;
      let t2 = h2 * 3600 + min2 * 60 + s2;
      let intervalSec = t2 - t1;
      if (intervalSec <= 0) intervalSec += 24 * 3600;

      if (intervalSec <= 0) return "Expired";

      // Compute remaining seconds relative to the current clock time so
      // the UI can show a live countdown.
      const nowDate = new Date(now);
      let nowSec =
        nowDate.getHours() * 3600 +
        nowDate.getMinutes() * 60 +
        nowDate.getSeconds();
      let elapsedSec = nowSec - t1;
      if (elapsedSec < 0) elapsedSec += 24 * 3600;
      const remainingSec = Math.max(0, intervalSec - elapsedSec);
      if (remainingSec <= 0) return "Expired";

      const minutes = Math.floor(remainingSec / 60);
      const seconds = remainingSec % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
  }

  // Prefer using expirationMinutes relative to activationTime (provider interval)
  if (typeof expirationMinutes === "number" && expirationMinutes > 0) {
    // Prefer explicit activationTime when available, otherwise fall back to createdAt
    const start = activationTime
      ? parseHeroDate(activationTime)
      : purchase.createdAt
        ? new Date(purchase.createdAt)
        : null;
    if (start && start instanceof Date && !Number.isNaN(start.getTime())) {
      const elapsedSec = Math.floor((now - start.getTime()) / 1000);
      const totalSec = Math.floor(expirationMinutes * 60);
      const remainingSec = Math.max(0, totalSec - elapsedSec);

      if (remainingSec <= 0) return "Expired";

      const hours = Math.floor(remainingSec / 3600);
      const minutes = Math.floor((remainingSec % 3600) / 60);
      const seconds = remainingSec % 60;

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }

      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
  }

  // Fallback to using activationEndTime absolute timestamp
  // Try activationEndTime first
  const exp = parseHeroDate(activationEndTime || purchase.activationEndTime);
  if (!exp) {
    // If no explicit end time, try computing from createdAt + expirationMinutes
    if (
      typeof expirationMinutes === "number" &&
      expirationMinutes > 0 &&
      purchase.createdAt
    ) {
      const start = new Date(purchase.createdAt);
      const fallbackExp = new Date(start.getTime() + expirationMinutes * 60000);
      const remainingMsFallback = fallbackExp - now;
      if (remainingMsFallback <= 0) return "Expired";
      const totalSecondsF = Math.max(0, Math.floor(remainingMsFallback / 1000));
      const hoursF = Math.floor(totalSecondsF / 3600);
      const minutesF = Math.floor((totalSecondsF % 3600) / 60);
      const secondsF = totalSecondsF % 60;
      if (hoursF > 0)
        return `${hoursF}:${minutesF.toString().padStart(2, "0")}:${secondsF.toString().padStart(2, "0")}`;
      return `${minutesF}:${secondsF.toString().padStart(2, "0")}`;
    }
    return "Unknown";
  }
  const remainingMs = exp - now;
  if (remainingMs <= 0) return "Expired";

  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "Unknown";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${minutes}m`;
};

const getExpirationDate = (purchase) => {
  const end = parseHeroDate(purchase.activationEndTime);
  if (end) return end;

  const start = parseHeroDate(purchase.activationTime);
  if (!start) return null;
  if (
    typeof purchase.expirationMinutes === "number" &&
    purchase.expirationMinutes > 0
  ) {
    return new Date(start.getTime() + purchase.expirationMinutes * 60000);
  }
  return null;
};

const formatDurationFromTimes = (minutes) => {
  if (minutes === null || minutes === undefined) return "Unknown";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${minutes}m`;
};

const getCanCancelStatus = (purchase) => {
  if (purchase.status !== "active") {
    return { canCancel: false, reason: null };
  }

  // Calculate seconds since purchase
  const secondsSincePurchase =
    (Date.now() - new Date(purchase.createdAt).getTime()) / 1000;
  const MIN_SECONDS = 120; // 2 minutes minimum

  if (secondsSincePurchase < MIN_SECONDS) {
    const waitSeconds = Math.ceil(MIN_SECONDS - secondsSincePurchase);
    return {
      canCancel: false,
      reason: `Wait ${Math.ceil(waitSeconds / 60)}m`,
      waitSeconds,
    };
  }

  return { canCancel: true, reason: null };
};

export const MyNumbersPage = () => {
  const dispatch = useDispatch();
  const { myNumbers, myNumbersLoading, cancelLoading } = useSelector(
    (state) => state.virtualNumbers,
  );
  const [now, setNow] = useState(Date.now());
  const [confirmCancelTarget, setConfirmCancelTarget] = useState(null);

  const myNumbersRef = useRef([]);
  const pollingRef = useRef(null);

  useEffect(() => {
    dispatch(fetchMyNumbers());
  }, [dispatch]);

  useEffect(() => {
    myNumbersRef.current = myNumbers;
  }, [myNumbers]);

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const pollStatus = async () => {
      if (cancelled) return;

      const activePending = myNumbersRef.current.filter(
        (purchase) =>
          purchase.status === "active" &&
          (!purchase.lastCheckedAt ||
            Date.now() - new Date(purchase.lastCheckedAt).getTime() > 3000),
      );

      if (activePending.length) {
        await Promise.all(
          activePending.map((purchase) =>
            dispatch(fetchSmsCode(purchase._id))
              .unwrap()
              .catch(() => null),
          ),
        );
      }

      if (cancelled) return;
      pollingRef.current = window.setTimeout(pollStatus, 4000);
    };

    pollStatus();
    return () => {
      cancelled = true;
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [dispatch]);

  const statusSummary = useMemo(
    () => ({
      active: myNumbers.filter((item) => item.status === "active").length,
      completed: myNumbers.filter((item) => item.status === "completed").length,
      expired: myNumbers.filter((item) => item.status === "expired").length,
      cancelled: myNumbers.filter((item) => item.status === "cancelled").length,
    }),
    [myNumbers],
  );

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("SMS code copied");
    } catch {
      toast.error("Unable to copy code");
    }
  };

  const confirmCancel = async () => {
    if (!confirmCancelTarget) return;
    try {
      await dispatch(cancelVirtualNumber(confirmCancelTarget._id)).unwrap();
      toast.success("Number cancelled — refund will be processed shortly.");
    } catch (err) {
      toast.error("Unable to cancel the number. Please try again.");
    } finally {
      setConfirmCancelTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-gradient p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">My Virtual Numbers</h1>
          <p className="text-gray-400">
            Track your active numbers, SMS codes, expiration countdowns, and
            refunds.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-4 mb-8">
          <GlassCard className="p-5 bg-white/80 border border-slate-200 shadow-sm">
            <p className="text-sm uppercase tracking-[0.2em] text-gray-500">
              Active
            </p>
            <p className="mt-3 text-3xl font-semibold text-luxury-900">
              {statusSummary.active}
            </p>
          </GlassCard>
          <GlassCard className="p-5 bg-white/80 border border-slate-200 shadow-sm">
            <p className="text-sm uppercase tracking-[0.2em] text-gray-500">
              Delivered
            </p>
            <p className="mt-3 text-3xl font-semibold text-luxury-900">
              {statusSummary.completed}
            </p>
          </GlassCard>
          <GlassCard className="p-5 bg-white/80 border border-slate-200 shadow-sm">
            <p className="text-sm uppercase tracking-[0.2em] text-gray-500">
              Refunded
            </p>
            <p className="mt-3 text-3xl font-semibold text-luxury-900">
              {statusSummary.expired}
            </p>
          </GlassCard>
          <GlassCard className="p-5 bg-white/80 border border-slate-200 shadow-sm">
            <p className="text-sm uppercase tracking-[0.2em] text-gray-500">
              Cancelled
            </p>
            <p className="mt-3 text-3xl font-semibold text-luxury-900">
              {statusSummary.cancelled}
            </p>
          </GlassCard>
        </div>

        {myNumbersLoading ? (
          <GlassCard className="p-12 text-center">
            <p className="text-gray-400">Loading purchased numbers...</p>
          </GlassCard>
        ) : myNumbers.length === 0 ? (
          <GlassCard className="p-12 text-center">
            <p className="text-gray-400">No purchased numbers found yet.</p>
          </GlassCard>
        ) : (
          <div className="flex flex-col sm:grid sm:grid-cols-1 gap-6 md:grid-cols-2">
            {myNumbers.map((purchase) => {
              const Icon = getServiceIcon(
                purchase.serviceName,
                purchase.serviceCode,
              );
              const expirationDate = getExpirationDate(purchase);
              const timeText = formatCountdown(purchase, now);
              const isWaiting =
                purchase.status === "active" && !purchase.smsCode;
              return (
                <GlassCard
                  key={purchase._id}
                  className="p-6 hover:shadow-xl transition-shadow"
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-3xl bg-cyan-500/10 text-cyan-600 flex items-center justify-center">
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-slate-900">
                            {purchase.serviceName}
                          </h2>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                            <CountryFlag
                              countryCode={purchase.countryCode}
                              className="h-5 w-7 rounded-sm object-cover"
                            />
                            <span>{purchase.countryName}</span>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${mapStatusClasses[purchase.status] || "bg-slate-100 text-slate-700"}`}
                      >
                        {purchase.status}
                      </span>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                          Phone
                        </p>
                        <p className="mt-3 text-lg font-semibold text-slate-900">
                          {purchase.phoneNumber}
                        </p>
                      </div>
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                          Expires in
                        </p>
                        {purchase.status === "cancelled" ? (
                          <p className="mt-3 text-lg font-bold text-rose-600">
                            Cancelled
                          </p>
                        ) : (
                          <>
                            <p className="mt-3 text-lg font-semibold text-slate-900">
                              {timeText}
                            </p>
                            <p className="mt-2 text-xs text-gray-500">
                              TTL{" "}
                              {formatDurationFromTimes(
                                Math.max(0, purchase.expirationMinutes || 0),
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FiMessageCircle className="w-4 h-4" />
                          <span>SMS Code</span>
                        </div>
                        {purchase.smsCode ? (
                          <span className="text-xs uppercase tracking-[0.2em] text-emerald-600">
                            Received
                          </span>
                        ) : (
                          <span className="text-xs uppercase tracking-[0.2em] text-gray-400">
                            Waiting
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div>
                          {purchase.smsCode ? (
                            <p className="text-3xl font-bold tracking-wider text-slate-900">
                              {purchase.smsCode}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500">
                              Waiting for the activation code to arrive. We’re
                              checking every 4 seconds...
                            </p>
                          )}
                        </div>
                        {purchase.smsCode && (
                          <button
                            onClick={() => handleCopy(purchase.smsCode)}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition"
                          >
                            <FiCopy className="w-4 h-4" /> Copy
                          </button>
                        )}
                      </div>
                      {purchase.smsText && (
                        <p className="mt-3 text-sm text-gray-500">
                          {purchase.smsText}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => dispatch(fetchSmsCode(purchase._id))}
                          disabled={!isWaiting}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiRefreshCcw className="w-4 h-4" />
                          Refresh Code
                        </button>
                        {purchase.status === "active" && !purchase.smsCode && (
                          <button
                            type="button"
                            onClick={() => setConfirmCancelTarget(purchase)}
                            disabled={
                              cancelLoading ||
                              !getCanCancelStatus(purchase).canCancel
                            }
                            title={
                              getCanCancelStatus(purchase).reason ||
                              "Cancel this number"
                            }
                            className="inline-flex items-center gap-2 rounded-xl bg-rose-600/10 px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiX className="w-4 h-4" />
                            {cancelLoading
                              ? "Cancelling..."
                              : getCanCancelStatus(purchase).reason || "Cancel"}
                          </button>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        Activation ID
                        <div className="mt-1 break-all font-medium text-slate-900">
                          {purchase.activationId}
                        </div>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
        {confirmCancelTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setConfirmCancelTarget(null)}
            />
            <div
              role="dialog"
              aria-modal="true"
              className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900">
                Cancel Virtual Number
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                You are about to cancel the activation of this number{" "}
                <span className="font-medium">
                  {confirmCancelTarget.phoneNumber}
                </span>
                . This will refund your purchase according to our refund policy.
                Do you want to proceed?
              </p>
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmCancelTarget(null)}
                  className="rounded-xl px-4 py-2 bg-slate-100 text-sm font-medium text-slate-700 hover:bg-slate-200"
                >
                  Keep Number
                </button>
                <button
                  type="button"
                  onClick={confirmCancel}
                  disabled={cancelLoading}
                  className="rounded-xl px-4 py-2 bg-rose-600 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
